/** Polling and SSE hooks.
 *
 * Everything here is a read-only projection: unmounting a component stops the
 * subscription and changes nothing about the run it was watching.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export function useAsync<T>(loader: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    loader()
      .then((result) => { if (!cancelled) { setData(result); setError(null); } })
      .catch((err) => { if (!cancelled) setError(err.message ?? "something went wrong"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, deps);

  useEffect(() => reload(), [reload]);
  return { data, error, loading, reload, setData };
}

export function usePolling<T>(
  loader: () => Promise<T>,
  intervalMs: number,
  active = true,
  deps: unknown[] = [],
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const saved = useRef(loader);
  saved.current = loader;

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let timer: number;

    const tick = async () => {
      try {
        const result = await saved.current();
        if (!cancelled) { setData(result); setError(null); }
      } catch (err: any) {
        if (!cancelled) setError(err.message ?? "lost connection");
      }
      if (!cancelled) timer = window.setTimeout(tick, intervalMs);
    };

    tick();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [intervalMs, active, ...deps]);

  return { data, error };
}

export function useInterval(callback: () => void, delay: number | null) {
  const saved = useRef(callback);
  saved.current = callback;
  useEffect(() => {
    if (delay === null) return;
    const id = window.setInterval(() => saved.current(), delay);
    return () => window.clearInterval(id);
  }, [delay]);
}

/** Wall-clock ms that advances on an interval. Use to keep timers ticking
 * between API polls; pass ``active=false`` to freeze (e.g. a finished run). */
export function useNow(active = true, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [active, intervalMs]);
  return now;
}
